"use client";

import Image from "next/image";
import { useState } from "react";
import { PlugStamp } from "../../components/PlugStamp";

type Props = {
  images: string[];
  alt: string;
  brand: string;
  colorway: string;
};

/**
 * PDP image gallery — large hero with a thumbnail strip underneath. Products
 * with a single image (no 360 angles available) render just the hero, no
 * thumbnails. Pure client state; no fetching.
 */
export function ProductGallery({ images, alt, brand, colorway }: Props) {
  const [active, setActive] = useState(0);
  const current = images[active] ?? null;

  return (
    <div>
      <div className="relative aspect-square overflow-hidden rounded-sm border border-[var(--color-bone)]/15 bg-white">
        {current ? (
          <Image
            key={current}
            src={current}
            alt={alt}
            fill
            priority
            fetchPriority="high"
            quality={70}
            sizes="(min-width: 768px) 56vw, 100vw"
            className="object-contain p-8"
          />
        ) : (
          <div className="halftone absolute inset-0 grid place-items-center bg-[var(--color-storm)]/30 font-mono text-[11px] uppercase tracking-[0.3em] text-[var(--color-bone-dim)]">
            {brand} · {colorway} · photo pending
          </div>
        )}
        <div className="pointer-events-none absolute bottom-4 right-4 text-[var(--color-ink)] opacity-25 mix-blend-multiply">
          <PlugStamp size={96} compact />
        </div>
      </div>

      {images.length > 1 && (
        <div className="mt-3 grid grid-cols-4 gap-3">
          {images.map((src, i) => (
            <button
              key={src}
              type="button"
              onClick={() => setActive(i)}
              aria-label={`View image ${i + 1} of ${images.length}`}
              aria-current={i === active}
              className={`relative aspect-square overflow-hidden rounded-sm border bg-white transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-foil)] ${
                i === active
                  ? "border-[var(--color-blood)]"
                  : "border-[var(--color-bone)]/15 hover:border-[var(--color-bone)]/40"
              }`}
            >
              <Image
                src={src}
                alt={`${alt} — view ${i + 1}`}
                fill
                quality={40}
                sizes="120px"
                className="object-contain p-2"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
